// src/components/CausesPanel.jsx
// "Causes": why items reached the agenda in the first place — a new state law,
// a budget cut, an expiring fund, a safety harm — rather than what they did.
//
// The driver is read from the transcript summary each meeting produces
// (agenda_items[].summary.context / .impact), which is where a sponsor's
// stated reason lands. That text states a reason for roughly half of what the
// board takes up; the rest is reported as not stated rather than guessed at,
// because inferring a motive the record doesn't contain would be inventing it.
//
// Figures shown beside each cause are quantities the record itself states
// (dollar amounts, day counts, page counts) — never computed or estimated,
// though amounts are rescaled to one format because the summaries mix
// notations ("$22,600,000" in one item, "$2.2M" in the next).
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import './CausesPanel.css';

/* ── Drivers, checked in order; first match wins ──
   Position-taking is checked before subject matter: a resolution urging the
   state to act is driven by that outside decision, whatever topic it names. */
const CAUSES = [
  {
    id: 'advocacy',
    label: 'To influence an outside decision',
    hint: 'Resolutions urging the state, federal government or another body to act',
    re: /\burg(e|es|ing)\b|resolution (supporting|opposing)|encourag(e|es|ing) (the )?(state|federal|congress|fcc|sfusd)|calls on|support for .{0,40}\bact\b/i,
  },
  {
    id: 'mandate',
    label: 'Required by state or federal law',
    hint: 'A new statute, court order or compliance deadline forces the city to act',
    re: /\bsenate bill\b|\bassembly bill\b|\bsb ?\d|\bab ?\d|brown act|required by (state )?law|state law criteria|court order|consent decree|settlement of lawsuits|comply with|in compliance with|state mandate/i,
  },
  {
    id: 'fiscal',
    label: 'Budget pressure or funding change',
    hint: 'Cuts, shortfalls, layoffs, or a change in outside funding',
    re: /budget (cut|shortfall|deficit|reduction)|service reduction|proposed reductions|proposed layoffs|federal funding|funding (cut|loss|risk|shortfall)|revenue (loss|shortfall|decline|projection)|deficit|structural (gap|deficit)/i,
  },
  {
    id: 'housing',
    label: 'Housing cost and displacement',
    hint: 'Shortage, homelessness, eviction risk, affordability',
    re: /displacement|homeless|eviction|rent burden|housing (shortage|crisis|affordability)|affordable housing|unhoused|tenant protection|supportive housing/i,
  },
  {
    id: 'safety',
    label: 'Public safety or health harm',
    hint: 'Crime, overdose, traffic injury, fire risk',
    re: /overdose|violence|violent crime|shooting|fatalit|traffic death|collision|illegal fireworks|drug (use|market|dealing)|crime rate|public health (emergency|crisis)|fire (risk|hazard|safety)|non-certified lithium/i,
  },
  {
    id: 'expiring',
    label: 'Authority expiring or up for renewal',
    hint: 'A fund, lease, contract or program lapses unless the board acts',
    re: /\bexpir|\bsunset\b|lapse|up for renewal|reauthoriz|extend the (term|deadline|sunset)|renew(al|ing)\b/i,
  },
  {
    id: 'demand',
    label: 'Service demand or capacity',
    hint: 'Waitlists, ridership, caseload, staffing shortfall',
    re: /demand for|waitlist|wait list|overcrowd|population growth|ridership|caseload|backlog|shortage of (staff|beds|slots)|capacity constraint/i,
  },
  {
    id: 'cleanup',
    label: 'Cleanup or modernization',
    hint: 'Obsolete rules, duplicate reporting, technical corrections',
    re: /obsolete|outdated|streamlin|code cleanup|reporting (requirement|burden|mandate)|defunct|technical (correction|amendment)|modernis|moderniz/i,
  },
  {
    // Checked last: standard business no external pressure prompted.
    id: 'routine',
    label: 'Routine operations',
    hint: 'Contracts, leases, appointments, grant acceptance, budget process',
    re: /\blease\b|\bcontract\b|amendment to (the )?agreement|appointment|reappoint|accept and expend|grant agreement|procurement|purchase|easement|annual report|minutes|budget and (annual )?salary ordinance/i,
  },
];

const classifyCause = (text) => {
  const t = String(text || '');
  for (const c of CAUSES) if (c.re.test(t)) return c.id;
  return 'unstated';
};

/* ── Quantities the record itself states ──
   Money is written inconsistently in these summaries — "$22,600,000" in one
   item, "$2.2M" in the next. The suffix MUST be part of the match: capturing
   "$2.2" and dropping the "M" understates the figure by a factor of a million,
   which is worse than showing nothing. Amounts are therefore parsed to a
   number and re-formatted to one scale, so figures across items are directly
   comparable and no suffix can be lost. */
const MONEY_RE = /\$\s?([\d,]+(?:\.\d+)?)\s*(million|billion|thousand|[mbk])?\b/gi;
const UNIT_RE = /\b(\d[\d,]*(?:\.\d+)?)[\s-]?(days?|years?|months?|units?|beds?|sites?|schools?|pages?|acres?)\b/gi;
const PCT_RE = /\b\d{1,3}(?:\.\d+)?\s?%/g;

const MULT = { m: 1e6, million: 1e6, b: 1e9, billion: 1e9, k: 1e3, thousand: 1e3 };

const fmtMoney = (n) => {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(n >= 1e8 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (n >= 1e4) return `$${Math.round(n / 1e3)}K`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

function quantities(text) {
  const t = String(text || '');
  const out = [];
  const seen = new Set();

  MONEY_RE.lastIndex = 0;
  let m;
  while ((m = MONEY_RE.exec(t)) !== null) {
    const base = parseFloat(m[1].replace(/,/g, ''));
    if (Number.isNaN(base)) continue;
    const suffix = (m[2] || '').toLowerCase();
    const value = base * (MULT[suffix] || 1);
    // Dedupe on the parsed value, so "$2.2M" and "$2,200,000" count once.
    const key = `m${Math.round(value)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ sort: value, text: fmtMoney(value) });
  }

  for (const re of [UNIT_RE, PCT_RE]) {
    re.lastIndex = 0;
    for (const raw of t.match(re) || []) {
      const v = raw.trim().replace(/\s+/g, ' ').replace(/-/g, ' ');
      const key = `u${v.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ sort: -1, text: v });
    }
  }

  // Largest money first — it is the figure a reader is looking for.
  return out.sort((a, b) => b.sort - a.sort).slice(0, 3).map((x) => x.text);
}

// Ordinal blue ramp — slices are ordered by size, so the colour carries
// magnitude, not identity. Validated for monotone lightness and contrast.
const RAMP = ['#104281', '#1c5cab', '#2a78d6', '#5598e7', '#86b6ef', '#b7d3f6'];

const polar = (cx, cy, r, deg) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const arcPath = (cx, cy, rOuter, rInner, startDeg, endDeg) => {
  const [x1, y1] = polar(cx, cy, rOuter, startDeg);
  const [x2, y2] = polar(cx, cy, rOuter, endDeg);
  const [x3, y3] = polar(cx, cy, rInner, endDeg);
  const [x4, y4] = polar(cx, cy, rInner, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${large} 0 ${x4} ${y4} Z`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y) return String(iso);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

function CausesPanel({ districtId }) {
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [year, setYear] = useState(null);
  const [openCause, setOpenCause] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('Meetings')
        .select('id, date, json_data')
        .eq('districtId', districtId)
        .order('date', { ascending: false })
        .limit(200);
      if (cancelled) return;
      setMeetings(error ? [] : data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  const items = useMemo(() => {
    const out = [];
    for (const m of meetings) {
      let p = m.json_data;
      for (let i = 0; i < 2 && typeof p === 'string'; i += 1) {
        try { p = JSON.parse(p); } catch { p = null; }
      }
      for (const it of (p && p.agenda_items) || []) {
        const ctx = (it.summary && it.summary.context) || '';
        const impact = (it.summary && it.summary.impact) || '';
        const text = `${ctx} ${impact}`;
        out.push({
          date: m.date,
          title: it.title || '',
          context: ctx,
          cause: classifyCause(text),
          qty: quantities(text),
        });
      }
    }
    return out;
  }, [meetings]);

  const years = useMemo(() => {
    const s = new Set(items.map((i) => String(i.date).slice(0, 4)).filter(Boolean));
    return Array.from(s).sort().reverse();
  }, [items]);

  useEffect(() => {
    if (!year && years.length) setYear(years[0]);
  }, [years, year]);

  const view = useMemo(() => {
    if (!year) return null;
    const inYear = items.filter((i) => String(i.date).slice(0, 4) === year);
    if (!inYear.length) return null;

    const byCause = new Map();
    for (const i of inYear) {
      if (!byCause.has(i.cause)) byCause.set(i.cause, []);
      byCause.get(i.cause).push(i);
    }
    const unstated = (byCause.get('unstated') || []).length;
    const stated = inYear.length - unstated;

    // The donut charts only items whose reason the record actually states —
    // a slice for "unknown" would swamp the chart and crowd out the finding.
    // Its size is reported in the line above instead.
    let rows = CAUSES
      .map((c) => ({ cause: c, list: byCause.get(c.id) || [] }))
      .filter((r) => r.list.length)
      .sort((a, b) => b.list.length - a.list.length);

    // Keep within the validated ramp: past six, fold the tail together.
    if (rows.length > RAMP.length) {
      const head = rows.slice(0, RAMP.length - 1);
      const tail = rows.slice(RAMP.length - 1);
      head.push({
        cause: {
          id: 'other_stated',
          label: 'Other stated reasons',
          hint: tail.map((t) => t.cause.label).join(', '),
        },
        list: tail.flatMap((t) => t.list),
      });
      rows = head;
    }

    let angle = 0;
    const slices = rows.map((r, i) => {
      const share = stated ? (r.list.length / stated) * 100 : 0;
      const sweep = stated ? (r.list.length / stated) * 360 : 0;
      const s = {
        ...r,
        n: r.list.length,
        share,
        start: angle,
        end: angle + sweep,
        color: RAMP[Math.min(i, RAMP.length - 1)],
      };
      angle += sweep;
      return s;
    });

    return { slices, stated, unstated, total: inYear.length };
  }, [items, year]);

  if (loading) return <div className="cz-card cz-status">Reading meeting records…</div>;
  if (!view) {
    return (
      <div className="cz-card cz-status">
        No processed meetings yet — this view appears once meetings are analyzed.
      </div>
    );
  }

  const cx = 130;
  const cy = 130;
  const only = view.slices.length === 1;
  const active = openCause && view.slices.find((s) => s.cause.id === openCause);

  return (
    <div className="cz-wrap">
      <div className="cz-card">
        <div className="cz-head-row">
          <div>
            <h3 className="cz-title">Why items reached the agenda — {year}</h3>
            <p className="cz-sub">
              The driver the record gives for each item the board took up: a new
              state law, a budget cut, an expiring fund, a safety harm. Click a
              slice to see the items behind it.
            </p>
          </div>
          {years.length > 1 && (
            <label className="cz-year">
              <span>Year</span>
              <select value={year || ''} onChange={(e) => { setYear(e.target.value); setOpenCause(null); }}>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
          )}
        </div>

        <div className="cz-coverage">
          <span className="cz-coverage-n">{view.stated}</span>
          <span>
            of {view.total} {year} agenda items state a reason. The chart covers
            those. The other <strong>{view.unstated}</strong> record what was
            decided but not what prompted it, and are left out rather than
            assigned a motive the record doesn&rsquo;t contain.
          </span>
        </div>

        <div className="cz-donut-row">
          <svg viewBox="0 0 260 260" className="cz-donut" role="img"
               aria-label={`Stated reasons for ${year} agenda items`}>
            {only ? (
              <circle cx={cx} cy={cy} r={92} fill="none"
                      stroke={view.slices[0].color} strokeWidth={44} />
            ) : (
              view.slices.map((s) => (
                <path
                  key={s.cause.id}
                  d={arcPath(cx, cy, 114, 70, s.start, s.end)}
                  fill={s.color}
                  stroke="#ffffff"
                  strokeWidth="2"
                  className="cz-slice"
                  onClick={() => setOpenCause(openCause === s.cause.id ? null : s.cause.id)}
                >
                  <title>{`${s.cause.label}: ${s.n} items (${s.share.toFixed(1)}%)`}</title>
                </path>
              ))
            )}
            <text x={cx} y={cy - 6} className="cz-donut-num">{view.stated}</text>
            <text x={cx} y={cy + 14} className="cz-donut-lbl">with a stated reason</text>
          </svg>

          <ul className="cz-legend">
            {view.slices.map((s) => {
              const withQty = s.list.filter((i) => i.qty.length).length;
              return (
                <li key={s.cause.id}>
                  <button
                    type="button"
                    className={`cz-legend-btn${openCause === s.cause.id ? ' active' : ''}`}
                    title={s.cause.hint}
                    onClick={() => setOpenCause(openCause === s.cause.id ? null : s.cause.id)}
                  >
                    <span className="cz-sw" style={{ background: s.color }} />
                    <span className="cz-legend-label">{s.cause.label}</span>
                    <span className="cz-legend-val">
                      {s.share.toFixed(0)}%
                      <span className="cz-legend-n">
                        {' '}· {s.n}
                        {withQty ? ` · ${withQty} with figures` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <p className="cz-note">
          Drivers are read by keyword from each item&rsquo;s transcript summary and
          are approximate — an item with several pressures is filed under the
          first that matches, in the order shown in the legend. Figures come
          from the record itself; amounts are rescaled to one format (the
          summaries mix &ldquo;$22,600,000&rdquo; and &ldquo;$2.2M&rdquo;) so
          they compare directly, but no figure is estimated or totalled.
        </p>
      </div>

      {active && (
        <div className="cz-card cz-detail">
          <div className="cz-detail-head">
            <h4>{active.cause.label}</h4>
            <span className="cz-detail-n">{active.n} item{active.n === 1 ? '' : 's'}</span>
            <button type="button" className="cz-close" onClick={() => setOpenCause(null)}>
              Close
            </button>
          </div>
          <p className="cz-detail-hint">{active.cause.hint}</p>
          <ul className="cz-items">
            {active.list.map((i, idx) => (
              <li key={`${i.date}-${idx}`}>
                <span className="cz-item-date">{fmtDate(i.date)}</span>
                <span className="cz-item-body">
                  <span className="cz-item-title">{i.title}</span>
                  {i.qty.length > 0 && (
                    <span className="cz-qty">
                      {i.qty.map((q) => <span className="cz-qty-tag" key={q}>{q}</span>)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CausesPanel;
